"use client";
import { Track } from "@/src/lib/tracking";

import { sendGtmOrApp } from "@utils/sendGtmOrApp";
import { getBridge } from "@/src/lib/bridge";

type FeatureTrackParams = Record<
  string,
  string | number | boolean | undefined | null
>;

interface FeatureTrackOptions {
  /** Adjust 事件 eventKey（映射到原生 twa-manifest eventTokens；未配置则原生自动跳过） */
  adjustEventKey?: string;
  /** OpenReplay 事件名（默认同 eventName） */
  openReplayName?: string;
}

function toCallbackParams(
  params?: FeatureTrackParams,
): Record<string, string | number | boolean> {
  if (!params) {
    return {};
  }
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) {
      continue;
    }
    out[k] =
      typeof v === "string" || typeof v === "number" || typeof v === "boolean"
        ? v
        : String(v);
  }
  return out;
}

/**
 * 统一 4 通道埋点：sendGtmOrApp(GA4/Firebase/GTM) + OpenReplay + Adjust + logbeacon。
 * 用于无 DOM 锚点的事件（hook/回调/路由）。Adjust 仅 App 内、且 eventKey 已在原生配置时生效。
 */
function emitFeatureTrackEvent(
  eventName: string,
  params?: FeatureTrackParams,
  opts?: FeatureTrackOptions,
): void {
  if (typeof window === "undefined") {
    return;
  }
  const cb = toCallbackParams(params);

  // 1. logbeacon（线上排查）
  try {
    Track.log("info", "FEATURE_TRACK", `${eventName}`, { cause: String(cb) });
  } catch {
    // ignore
  }

  // 2. sendGtmOrApp（GA4 / Firebase / GTM）
  try {
    sendGtmOrApp({ event: eventName, ...cb });
  } catch {
    // ignore
  }

  // 3. OpenReplay（会话回放事件）—— 走 `Track.event` 而非裸调 `emitOpenReplayEvent`，
  // 否则这条漏斗的所有事件都没有 envelope（`event_type`/`scene`/`app_run_id`/`seq`/`container`），
  // 后台按字段筛不出来、串不进链路。本函数是 §0「另起单一出口同时发」的多通道门面，
  // 但它的 OpenReplay 那一腿仍然必须走 `Track`。
  try {
    Track.event(opts?.openReplayName ?? eventName, cb);
  } catch {
    // ignore
  }

  // 4. Adjust 事件（App 内 + eventKey 已配置才生效；未配置原生自动 skip）
  if (opts?.adjustEventKey) {
    try {
      const bridge = getBridge();
      bridge?.adjustTrackEvent?.({
        eventKey: opts.adjustEventKey,
        callbackParams: cb,
      });
    } catch {
      // ignore
    }
  }
}

/**
 * 广告钩子用户落 Brief 首页（US1 S1.1）。
 * ad_source ∈ {alpha_pick, portfolio_check} 时触发。
 */
export function trackAdLandHome(adSource: string | null | undefined): void {
  emitFeatureTrackEvent(
    "ad_land_home",
    { ad_source: adSource ?? "unknown" },
    {
      // Adjust eventToken 待后在 twa-manifest eventTokens 配置（key 如 ad_land_home）
      adjustEventKey: "ad_land_home",
    },
  );
}
